import React from 'react';
import { useTheme } from '../../context/ThemeContext';

/**
 * Refined status pill — dot prefix + tinted background.
 *
 * Designed for fast row scanning in dense data tables. The component is
 * theme-aware via `useTheme()` so the same call site renders correctly
 * in light AND dark mode without `dark:` variant prefixes (this app's
 * theming toggles a `.theme-light` class on a wrapper, not the document
 * root, so Tailwind's class-based dark mode isn't reliable here).
 *
 * Usage:
 *   <StatusPill value="active" label="Active" />
 *   <StatusPill value="maintenance" size="sm" />
 */

// Tone presets — each renders both a light-mode tint (50/700/200) and a
// dark-mode tint (500/15, 300, 500/30) so the visual hierarchy stays the
// same regardless of theme.
const TONES = {
  emerald: {
    light: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    dark:  'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    dot:   'bg-emerald-500',
  },
  amber: {
    light: 'bg-amber-50 text-amber-700 ring-amber-200',
    dark:  'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    dot:   'bg-amber-500',
  },
  blue: {
    light: 'bg-blue-50 text-blue-700 ring-blue-200',
    dark:  'bg-blue-500/15 text-blue-300 ring-blue-500/30',
    dot:   'bg-blue-500',
  },
  red: {
    light: 'bg-red-50 text-red-700 ring-red-200',
    dark:  'bg-red-500/15 text-red-300 ring-red-500/30',
    dot:   'bg-red-500',
  },
  zinc: {
    light: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
    dark:  'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30',
    dot:   'bg-zinc-400',
  },
  violet: {
    light: 'bg-violet-50 text-violet-700 ring-violet-200',
    dark:  'bg-violet-500/15 text-violet-300 ring-violet-500/30',
    dot:   'bg-violet-500',
  },
};

// Canonical mapping from status value → tone. Unknown values fall back to
// `zinc` so renders are never broken when a new status type is added.
const STATUS_TONE = {
  active: 'emerald',
  available: 'emerald',
  ok: 'emerald',
  passed: 'emerald',
  online: 'emerald',

  maintenance: 'amber',
  servicing: 'amber',
  scheduled: 'amber',
  pending: 'amber',
  in_progress: 'amber',

  inspection_due: 'blue',
  due: 'blue',
  reviewing: 'blue',
  open: 'blue',

  out_of_service: 'red',
  failed: 'red',
  critical: 'red',
  overdue: 'red',
  offline: 'red',

  idle: 'zinc',
  inactive: 'zinc',
  unknown: 'zinc',

  retired: 'violet',
  decommissioned: 'violet',
  archived: 'violet',
};

const SIZE_CLASSES = {
  xs: 'h-5 px-2 text-[10px] gap-1 ring-1',
  sm: 'h-6 px-2.5 text-xs gap-1.5 ring-1',
  md: 'h-7 px-3 text-xs gap-1.5 ring-1',
};

const DOT_SIZE = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
};

const humanise = (value) =>
  (value || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const StatusPill = ({
  value,
  label,
  size = 'sm',
  tone: toneOverride,
  className = '',
  ...rest
}) => {
  const { isLight } = useTheme();
  const toneKey = toneOverride || STATUS_TONE[(value || '').toLowerCase()] || 'zinc';
  const tone = TONES[toneKey] || TONES.zinc;
  const themeClasses = isLight ? tone.light : tone.dark;
  const display = label || humanise(value);

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full
                  ring-inset whitespace-nowrap
                  ${SIZE_CLASSES[size]} ${themeClasses} ${className}`}
      data-testid={rest['data-testid'] || `status-pill-${value || 'unknown'}`}
      {...rest}
    >
      <span className={`rounded-full ${DOT_SIZE[size]} ${tone.dot}`} aria-hidden="true" />
      {display}
    </span>
  );
};

export default StatusPill;

